import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2, ArrowLeft, Sparkles, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DocumentProcessor({ user, logout }) {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [exportFormat, setExportFormat] = useState("dita");
  const [exportOutput, setExportOutput] = useState("pdf");
  const [exporting, setExporting] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem("standari_token");
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      setDocumentId(response.data.document_id);
      toast.success("Document uploaded successfully!");
      
      // Auto-start processing
      await handleProcess(response.data.document_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleProcess = async (docId) => {
    setProcessing(true);
    try {
      const token = localStorage.getItem("standari_token");
      const response = await axios.post(`${API}/documents/${docId}/process`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setProcessedData(response.data);
      toast.success("Document processed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Processing failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = () => {
    if (processedData?.processed_content_id) {
      navigate(`/editor/${processedData.processed_content_id}`);
    }
  };

  const handleExport = async () => {
    if (!processedData?.processed_content_id) return;

    setExporting(true);
    try {
      const token = localStorage.getItem("standari_token");
      const response = await axios.post(
        `${API}/export-v2`,
        {
          content_id: processedData.processed_content_id,
          format: exportFormat,
          output_format: exportOutput,
          options: {}
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );

      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const extension = exportOutput === "zip" ? "zip" : exportOutput === "xml" ? "xml" : exportOutput === "docx" ? "docx" : exportOutput === "html" ? "html" : "pdf";
      link.setAttribute("download", `export_${exportFormat}_${Date.now()}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Exported as ${exportFormat.toUpperCase()} ${exportOutput.toUpperCase()}`);
    } catch (error) {
      const errorMsg = error.response?.status === 403
        ? "Upgrade required for this export format"
        : error.response?.data?.detail || "Export failed";
      toast.error(errorMsg);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen gradient-bg" data-testid="processor-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-2">
                <FileText className="h-8 w-8 text-blue-600" />
                <span className="text-2xl font-heading font-bold gradient-text">Standari</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-slate-900 mb-2">Process Document</h1>
          <p className="text-lg text-slate-600">Upload and transform your documents</p>
        </div>

        {/* Upload Section */}
        {!documentId && (
          <Card className="doc-card mb-8" data-testid="upload-section">
            <CardHeader>
              <CardTitle className="font-heading">Upload Document</CardTitle>
              <CardDescription>Supports PDF, Word, PowerPoint, images, and more</CardDescription>
            </CardHeader>
            <CardContent>
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-12 text-center transition-all ${
                  dragActive ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-blue-400"
                }`}
                data-testid="drop-zone"
              >
                <Upload className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                {file ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center space-x-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold text-slate-900" data-testid="selected-filename">{file.name}</span>
                    </div>
                    <p className="text-sm text-slate-500">{(file.size / 1024).toFixed(2)} KB</p>
                    <div className="flex gap-4 justify-center">
                      <Button onClick={handleUpload} disabled={uploading} className="btn-primary" data-testid="upload-btn">
                        {uploading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          "Upload & Process"
                        )}
                      </Button>
                      <Button variant="outline" onClick={() => setFile(null)} disabled={uploading} data-testid="clear-file-btn">
                        Clear
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-slate-600 mb-4">Drop your document here</p>
                    <p className="text-sm text-slate-500 mb-4">PDF, Word, up to 50MB<br />We'll scan for the matching standard in seconds</p>
                    <input
                      type="file"
                      id="file-input"
                      className="hidden"
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.png,.jpg,.jpeg"
                      data-testid="file-input"
                    />
                    <Button variant="outline" onClick={() => document.getElementById("file-input").click()} data-testid="browse-btn">
                      Browse Files
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Processing Status */}
        {processing && (
          <Card className="doc-card mb-8" data-testid="processing-status">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <Loader2 className="h-12 w-12 text-blue-600 mx-auto animate-spin" />
                <h3 className="text-xl font-semibold font-heading">Reading your document...</h3>
                <p className="text-slate-600">Looking for chapter codes, data modules, and references</p>
                <Progress value={45} className="w-full" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Processing Results */}
        {processedData && !processing && (
          <Card className="doc-card" data-testid="processing-results">
            <CardHeader>
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <CardTitle className="font-heading">Matched! Ready to proceed.</CardTitle>
              </div>
              <CardDescription>Your document has been analyzed and structured</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Structure Analysis */}
              {processedData.preview?.structure && (
                <div data-testid="structure-preview">
                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center">
                    <Sparkles className="h-4 w-4 mr-2 text-blue-600" />
                    Document Structure
                  </h3>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <pre className="text-sm text-slate-700 whitespace-pre-wrap">
                      {JSON.stringify(processedData.preview.structure, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* STE Compliance */}
              {processedData.preview?.ste_compliance && (
                <div data-testid="ste-compliance-preview">
                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 text-yellow-600" />
                    STE Compliance
                  </h3>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <pre className="text-sm text-slate-700 whitespace-pre-wrap">
                      {JSON.stringify(processedData.preview.ste_compliance, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Standards Mapping */}
              {processedData.preview?.standards_mapping && (
                <div data-testid="standards-mapping-preview">
                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center">
                    <FileText className="h-4 w-4 mr-2 text-blue-600" />
                    Standards Mapping
                  </h3>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <pre className="text-sm text-slate-700 whitespace-pre-wrap">
                      {JSON.stringify(processedData.preview.standards_mapping, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Compliance Keywords Reference */}
              {processedData.preview?.cleaned_text && (
                <div data-testid="compliance-keywords-preview">
                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center">
                    <CheckCircle2 className="h-4 w-4 mr-2 text-blue-600" />
                    Compliance Keywords Reference
                  </h3>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="space-y-2 text-sm text-slate-700">
                      <p><strong>SHALL/MUST:</strong> Mandatory requirements</p>
                      <p><strong>SHOULD:</strong> Recommended practices</p>
                      <p><strong>MAY:</strong> Optional features</p>
                      <p><strong>WILL:</strong> Informational statements</p>
                    </div>
                    <p className="text-xs text-slate-600 mt-3 pt-3 border-t border-slate-200">
                      Keywords follow {processedData.preview.standard || 'detected standard'} compliance rules (RFC 2119)
                    </p>
                  </div>
                </div>
              )}

              {/* Glossary & Components Info */}
              {processedData.preview?.glossary_count > 0 && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-900 mb-2">Extracted Intelligence</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-green-700">Glossary Terms: </span>
                      <span className="font-bold">{processedData.preview.glossary_count}</span>
                    </div>
                    <div>
                      <span className="text-green-700">Reusable Components: </span>
                      <span className="font-bold">{processedData.preview.reusable_components_count}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Export Options */}
              <div className="border-t border-slate-200 pt-6 mt-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center">
                  <Download className="h-5 w-5 mr-2 text-blue-600" />
                  Export Document
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Standard</label>
                    <Select value={exportFormat} onValueChange={setExportFormat}>
                      <SelectTrigger data-testid="export-format-select">
                        <SelectValue placeholder="Select standard" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dita">DITA 1.3</SelectItem>
                        <SelectItem value="s1000d">S1000D Issue 6.1</SelectItem>
                        <SelectItem value="mil_40051">MIL-STD-40051</SelectItem>
                        <SelectItem value="mil_38784">MIL-STD-38784</SelectItem>
                        <SelectItem value="iso_15288">ISO/IEC 15288</SelectItem>
                        <SelectItem value="ata_ispec2200">ATA iSpec 2200</SelectItem>
                        <SelectItem value="hl7_fhir_r5">HL7 FHIR R5</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Output Format</label>
                    <Select value={exportOutput} onValueChange={setExportOutput}>
                      <SelectTrigger data-testid="export-output-select">
                        <SelectValue placeholder="Select format" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pdf">PDF Document</SelectItem>
                        <SelectItem value="docx">Word Document</SelectItem>
                        <SelectItem value="xml">XML Source</SelectItem>
                        <SelectItem value="html">HTML Preview</SelectItem>
                        <SelectItem value="zip">Full Package (ZIP)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={handleExport} 
                      disabled={exporting} 
                      className="btn-primary w-full"
                      data-testid="export-btn"
                    >
                      {exporting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Exporting...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Export Now
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Advanced standards (S1000D, MIL-STD, ATA, FHIR) require Enterprise tier.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4">
                <Button onClick={handleEdit} className="btn-primary flex-1" data-testid="open-editor-btn">
                  Open in Editor
                </Button>
                <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="back-dashboard-btn">
                  Back to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}